import { TextFieldModule } from '@angular/cdk/text-field'
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { last, slice } from 'lodash/fp'
import { TooltipModule } from 'primeng/tooltip'

import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import { ConversationThreadExecutionPlanActionTargetType } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  FileExtension,
  FileMimeTypeLabel,
  MessageType,
  ShortcutIconName,
} from '@flowx/designer/modules/ai/types/ai.types'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import * as NodeActionsSelectors from '@flowx/designer/shared/store/selectors/node-actions.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import { templatesConfigsById } from '@flowx/designer/shared/store/selectors/template-config.selectors'

import { resetInputFileValueFromEvent } from '../../../../common/ai.utils'
import { AiConversationService } from '../../../../conversation/ai-conversation.service'
import { AiChatBubbleActions, TypeAreaActions } from '../../../../conversation/conversation.actions'
import { ConversationSelectors } from '../../../../conversation/conversation.selectors'
import { AiService } from '../../../../store/ai.service'
import {
  AiShortcutComponent,
  IconType,
  ShortcutConfig,
} from '../../../command-palette/components/shortcut/shortcut.component'
import { AiChatAgentMarkerComponent } from '../ai-chat-agent-marker/ai-chat-agent-marker.component'

@Component({
  selector: 'flx-ai-type-area',
  templateUrl: './type-area.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlxIconModule,
    TextFieldModule,
    AiShortcutComponent,
    TooltipModule,
    AiChatAgentMarkerComponent,
  ],
})
export class AiTypeAreaComponent implements AfterViewInit {
  #aiService = inject(AiService)
  #store = inject(Store)
  #actions$ = inject(Actions)
  #destroyRef = inject(DestroyRef)

  #imageFiles = this.#store.selectSignal(ConversationSelectors.uploadedImageFiles)
  #bpmnFiles = this.#store.selectSignal(ConversationSelectors.uploadedBpmnFiles)
  #aiConversationService = inject(AiConversationService)

  #bpmnFilesSelected = computed(() => !!this.#bpmnFiles().length)
  #loading = this.#store.selectSignal(ConversationSelectors.loading)
  #mainOutcome = this.#store.selectSignal(ConversationSelectors.mainOutcome)
  #agentProcessingMessage = this.#store.selectSignal(ConversationSelectors.agentProcessingMessage)
  #processDefinition = signal<ProcessDefinition | null>(null)

  hint = input<string>('')
  textarea = viewChild<ElementRef>('textarea')

  typeControl = new FormControl('', [Validators.required, Validators.maxLength(10000)])

  submitShortcut: ShortcutConfig['icons'] = [
    { type: IconType.ICON, name: ShortcutIconName.ArrowUp },
  ]

  showUpload = computed(
    () =>
      // Upload should be shown only when:
      // - No loading/streaming in progress
      // - No processing messages in current thread
      // - No AI outcome in current thread
      !this.#loading() &&
      !this.#agentProcessingMessage() &&
      !this.#mainOutcome() &&
      this.currentMessageAgent() &&
      this.allowedFileTypes().length > 0
  )
  isUploadDisabled = this.#store.selectSignal(ConversationSelectors.isUploadDisabled)
  allowedFileTypes = this.#store.selectSignal(ConversationSelectors.allowedFileTypes)
  isMultipleUploadAllowed = this.#store.selectSignal(ConversationSelectors.isMultipleUploadAllowed)

  uploadFileTooltip = computed(() => {
    const allowedFileTypes = this.allowedFileTypes().filter((type) => type !== FileExtension.BPMN)

    if (this.isUploadDisabled()) {
      return ''
    }

    const allowedFileTypesStr =
      allowedFileTypes.length === 1
        ? FileMimeTypeLabel[allowedFileTypes[0]]
        : `${slice(0, -2, allowedFileTypes)
            .map((type) => FileMimeTypeLabel[type])
            .join(', ')} and ${FileMimeTypeLabel[last(allowedFileTypes)]}`

    return `Upload ${allowedFileTypesStr} files to use as context for your prompt.`
  })
  currentMessageAgent = this.#store.selectSignal(ConversationSelectors.currentMessageAgent)
  activeAgentTarget = this.#store.selectSignal(ConversationSelectors.activeAgentTarget)
  nodes = this.#store.selectSignal(nodesSelector)

  agentMarkerLabel = computed(() => {
    const activeAgentTarget = this.activeAgentTarget()
    const currentMessageAgent = this.currentMessageAgent()
    if (!activeAgentTarget || !currentMessageAgent) {
      return null
    }

    if (
      currentMessageAgent.agent === AgentName.AiDeveloper &&
      activeAgentTarget.targetType === ConversationThreadExecutionPlanActionTargetType.BusinessRule
    ) {
      const actionId = parseInt(activeAgentTarget.businessRule?.actionId, 10)
      const node = this.nodes().find(
        (node) => node.id === parseInt(activeAgentTarget.businessRule?.nodeId, 10)
      )
      const nodeActionsById = this.#store.selectSignal(
        NodeActionsSelectors.nodeActionsById(node?.id)
      )()
      const action = nodeActionsById?.find((action) => action.id === actionId)
      return `Interact with code on ${action?.name}`
    }

    if (
      currentMessageAgent.agent === AgentName.AiDeveloper &&
      activeAgentTarget.targetType === ConversationThreadExecutionPlanActionTargetType.Expression
    ) {
      const templateId = activeAgentTarget.expression?.elementId
      const node = this.nodes().find(
        (node) => node.id === parseInt(activeAgentTarget.expression?.nodeId, 10)
      )
      const navigationAreaId = node?.navigationAreaId
      if (navigationAreaId && templateId) {
        const templateConfig = this.#store.selectSignal(
          templatesConfigsById(navigationAreaId, parseInt(templateId, 10))
        )()
        if (templateConfig) {
          return `Interact with code on ${templateConfig?.customIdentifier || templateConfig?.componentIdentifier}`
        }
      }
      return `Interact with code`
    }

    if (
      currentMessageAgent.agent === AgentName.AiDeveloper &&
      activeAgentTarget.targetType ===
        ConversationThreadExecutionPlanActionTargetType.CustomComponent
    ) {
      return `Interact with code on ${activeAgentTarget.customComponent?.name ?? 'custom component'}`
    }

    if (
      currentMessageAgent.agent === AgentName.AiDesigner &&
      activeAgentTarget.targetType === ConversationThreadExecutionPlanActionTargetType.Node
    ) {
      const targetId = activeAgentTarget.node?.templateConfigId
      const navigationAreaId = activeAgentTarget.node?.navigationAreaId
      const node = this.nodes().find((node) => node.id === parseInt(activeAgentTarget.node?.id, 10))

      if (targetId && navigationAreaId && parseInt(targetId, 10) > 0) {
        const templateConfig = this.#store.selectSignal(
          templatesConfigsById(navigationAreaId, parseInt(targetId, 10))
        )()

        if (templateConfig) {
          return `Generate UI in ${templateConfig.customIdentifier || templateConfig.componentIdentifier}`
        }
      }

      return `Generate UI in ${node?.name}`
    }

    if (
      currentMessageAgent.agent === AgentName.AiDesigner &&
      activeAgentTarget.targetType ===
        ConversationThreadExecutionPlanActionTargetType.ReusableUiTemplate
    ) {
      return `Generate UI in ${activeAgentTarget.name || 'template'}`
    }

    if (
      currentMessageAgent.agent === AgentName.AiAnalyst &&
      [AgentAction.CreateProcess, AgentAction.CreateProcessFromImage].includes(
        currentMessageAgent.action as AgentAction
      )
    ) {
      const processDefinition = this.#processDefinition()
      return `Generate process in ${processDefinition?.resourceName || 'loading...'}`
    }

    if (
      currentMessageAgent.agent === AgentName.AiDeveloper &&
      activeAgentTarget.targetType ===
        ConversationThreadExecutionPlanActionTargetType.ReusableFunction
    ) {
      const name = activeAgentTarget.name
      return name ? `Interact with code on ${name}` : 'Interact with reusable function'
    }

    if (currentMessageAgent.agent === AgentName.AiArchitect) {
      const verb =
        currentMessageAgent.action === AgentAction.ExtendDataModel ? 'Extending' : 'Generating'
      return `${verb} data types on ${activeAgentTarget.name || 'this project'}`
    }

    return null
  })

  get promptInvalidLengthMsg(): string {
    const { actualLength, requiredLength } = this.typeControl.errors?.maxlength || {}

    return this.typeControl.errors?.maxlength
      ? `Character Limit Exceeded ${actualLength} / ${requiredLength}`
      : null
  }

  constructor() {
    this.#onCopyToChatInputField()
    this.#setupProcessDefinitionSignal()

    effect(() => {
      const bpmnFilesSelected = this.#bpmnFilesSelected()
      if (bpmnFilesSelected) {
        this.typeControl.disable()
      } else {
        this.typeControl.enable()
      }
    })
  }

  ngAfterViewInit(): void {
    this.textarea()?.nativeElement.focus({ preventScroll: true })
  }

  filterKeyEvents(event: KeyboardEvent): void {
    if (!this.canSubmitPrompt()) {
      return
    }

    const { key, shiftKey, metaKey } = event
    const submitKeyEv = key.toLowerCase() === 'enter' && !shiftKey && !metaKey
    const newLineKeyEv = key.toLowerCase() === 'enter' && metaKey

    if (newLineKeyEv) {
      this.typeControl.setValue(this.typeControl.value + '\n')
    }

    if (submitKeyEv) {
      event.preventDefault()
      event.stopPropagation()

      this.submitTypeControl()
    }
  }

  // TODO: Refactor this to be a computed signal
  canSubmitPrompt(): boolean {
    const imagesSelected = !!this.#imageFiles().length
    const bpmnFilesSelected = this.#bpmnFilesSelected()
    const textValid = this.typeControl.valid
    const aiPending = this.#store.selectSignal(ConversationSelectors.loading)()
    const isDataModelKeepStep =
      this.#store.selectSignal(ConversationSelectors.isUiOutcomeFinished)() &&
      !this.#store.selectSignal(ConversationSelectors.isDataModelOutcomeFinished)() &&
      !!this.#store.selectSignal(ConversationSelectors.dataModelOutcome)()

    return (imagesSelected || bpmnFilesSelected || textValid) && !aiPending && !isDataModelKeepStep
  }

  // TODO: Rename imageFiles to files everywhere (components and most probably store)
  submitTypeControl(): void {
    if (!this.canSubmitPrompt()) {
      return
    }

    const files = [...this.#imageFiles(), ...this.#bpmnFiles()].map((file) => file.file)
    if (this.canSubmitPrompt()) {
      this.#store.dispatch(
        TypeAreaActions.promptSubmit({
          text: this.typeControl.value,
          files,
        })
      )

      // Always use submitMessage - it handles file uploads internally
      // The files are already in the store from the fileUploaded action
      this.#aiConversationService
        .submitMessage({
          prompt: this.typeControl.value || '', // Allow empty message when files are attached
          type: MessageType.UserMsg,
        })
        .subscribe()

      this.#resetAndFocusTextarea()
    }
  }

  onFileSelected(event: Event): void {
    const files = Array.from((event.target as HTMLInputElement).files ?? [])
    this.#store.dispatch(TypeAreaActions.fileSelected({ files }))
    resetInputFileValueFromEvent(event)
  }

  // Clipboard paste: route pasted files (e.g. screenshots) through the same upload pipeline
  // as the picker — allowed-type gating included. Accepts BOTH text and image: when the
  // clipboard also carries text the default text insertion is kept; the default is only
  // suppressed for a pure file paste (so a stray file name isn't inserted as text).
  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files ?? [])
    if (!files.length || !this.showUpload() || this.isUploadDisabled()) {
      return
    }
    if (!event.clipboardData.getData('text/plain')) {
      event.preventDefault()
    }
    this.#store.dispatch(TypeAreaActions.fileSelected({ files }))
  }

  // Drag & drop (e.g. from Finder): same upload pipeline as the picker/paste.
  // dragover must be cancelled for the browser to allow dropping at all.
  onDragOver(event: DragEvent): void {
    if (event.dataTransfer?.types.includes('Files')) {
      event.preventDefault()
      if (!this.showUpload() || this.isUploadDisabled()) {
        event.dataTransfer.dropEffect = 'none'
      }
    }
  }

  onDrop(event: DragEvent): void {
    const files = Array.from(event.dataTransfer?.files ?? [])
    if (files.length) {
      event.preventDefault() // keep the browser from navigating to / opening the file
    }
    if (!files.length || !this.showUpload() || this.isUploadDisabled()) {
      return
    }
    this.#store.dispatch(TypeAreaActions.fileSelected({ files }))
  }

  #onCopyToChatInputField(): void {
    this.#actions$
      .pipe(ofType(AiChatBubbleActions.copyToChatInputField), takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ text }) => {
        this.typeControl.setValue(text)
      })
  }

  #resetAndFocusTextarea(): void {
    this.typeControl.reset()
    this.textarea()?.nativeElement.focus()
  }

  #setupProcessDefinitionSignal(): void {
    const unsubscribe = ProcessEffector.processDefinition$.watch((processDefinition) => {
      this.#processDefinition.set(processDefinition)
    })

    this.#destroyRef.onDestroy(() => {
      unsubscribe()
    })
  }
}
